/* Garage filter — hides product cards that don't fit the customer's saved
 * scooter, with a toggle to switch it on/off per collection page.
 *
 * The card's compatibility_summary lives on `data-fitment` (set by
 * snippets/card-product.liquid). Cards without that attribute are treated as
 * "universal" — they're always shown.
 *
 * UI: a sticky toggle is injected at the top of each collection grid. It
 * controls the filter state, persisted in sessionStorage so it survives
 * pagination but resets between sessions.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'evscooter:garage:v1';
  const TOGGLE_KEY = 'evscooter:garage:filter-on';

  // Strings injected by snippets/garage-i18n.liquid (translatable per locale
  // via Shopify Admin → Translations). German fallback for safety only.
  const I18N = window.evGarageI18n || {};
  const T = {
    filter_on:                 I18N.filterOnLabel        || 'Nur passende anzeigen',
    filter_status_off:         I18N.filterStatusOff      || 'Filter aus',
    filter_status_specific:    I18N.filterStatusSpecific || '{specific} passen zu deinem {scooter} + {universal} universell',
    filter_status_no_specific: I18N.filterStatusNoSpecific || 'Keine speziellen Teile für deinen {scooter} — {universal} passen universell',
    filter_status_none:        I18N.filterStatusNone     || 'Keine passenden Teile für deinen {scooter}',
    no_garage:                 I18N.filterNoGarageHint   || 'Wähle deinen Scooter, um nur passende Teile zu sehen.',
    no_garage_cta:             I18N.chooseScooter        || 'Scooter wählen',
  };

  function readGarage() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch (_) { return null; }
  }
  function readToggle() {
    return sessionStorage.getItem(TOGGLE_KEY) === '1';
  }
  function writeToggle(on) {
    sessionStorage.setItem(TOGGLE_KEY, on ? '1' : '0');
  }

  // Filler tokens to strip on both sides so "Xiaomi 1s" matches "Xiaomi Mi 1s".
  const FILLER = new Set(['mi', 'the', 'electric', 'scooter', 'series']);
  function tokenize(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[(),]/g, ' ')
      .split(/\s+/)
      .filter((t) => t && !FILLER.has(t));
  }
  function tokensFit(targetTokens, candidate) {
    if (!targetTokens.length) return false;
    const candTokens = tokenize(candidate);
    if (!candTokens.length) return false;
    const candSet = new Set(candTokens);
    return targetTokens.every((t) => candSet.has(t));
  }

  function scooterNames(scooter) {
    const names = [
      scooter.variantName,
      scooter.model,
      ...(Array.isArray(scooter.aliases) ? scooter.aliases : []),
      scooter.brandName,
    ].filter(Boolean);
    return Array.from(new Set(names));
  }

  // Returns 'specific' | 'universal' | 'no-match' so the toolbar can show
  // counts separately. 'universal' = card has no compatibility data at all
  // (matches everything); 'specific' = card has data AND the saved scooter
  // matches; 'no-match' = card has data but the scooter doesn't match.
  function classifyCard(card, scooter) {
    const legacy = card.getAttribute('data-fitment');
    const summary = card.getAttribute('data-fitment-summary');
    if (!legacy && !summary) return 'universal';

    if (legacy) {
      try {
        const arr = JSON.parse(legacy);
        if (!Array.isArray(arr) || arr.length === 0) return 'universal';
        const targets = scooterNames(scooter).map(tokenize).filter((t) => t.length);
        if (!targets.length) return 'universal';
        for (const target of targets) {
          for (const s of arr) {
            if (tokensFit(target, s)) return 'specific';
          }
        }
        return 'no-match';
      } catch (_) { /* fall through */ }
    }
    if (summary) {
      try {
        const arr = JSON.parse(summary);
        if (!Array.isArray(arr) || arr.length === 0) return 'universal';
        const names = scooterNames(scooter);
        for (const e of arr) {
          if (!e || e.brandName !== scooter.brandName) continue;
          if (e.model && e.model !== scooter.model && !names.includes(e.model)) continue;
          if (!Array.isArray(e.variants) || e.variants.length === 0) return 'specific';
          if (names.some((name) => e.variants.includes(name))) return 'specific';
        }
        return 'no-match';
      } catch (_) { /* fall through */ }
    }
    return 'universal';
  }

  function getGrids() {
    return Array.from(document.querySelectorAll('[data-collection-grid], ul[id^="product-grid"], #product-grid, .product-grid'));
  }

  function injectToolbar(grid) {
    if (grid.previousElementSibling && grid.previousElementSibling.hasAttribute('data-garage-toolbar')) {
      return grid.previousElementSibling;
    }
    const bar = document.createElement('div');
    bar.className = 'garage-toolbar';
    bar.setAttribute('data-garage-toolbar', '');
    bar.innerHTML = `
      <label class="garage-toolbar__toggle">
        <input type="checkbox" data-garage-toggle>
        <span>${T.filter_on}</span>
      </label>
      <span class="garage-toolbar__status" data-garage-status></span>
      <button type="button" class="garage-toolbar__cta" data-garage-open hidden>${T.no_garage_cta}</button>
    `;
    grid.parentNode.insertBefore(bar, grid);
    return bar;
  }

  function applyFilter(grid, bar) {
    const scooter = readGarage();
    const toggle = bar.querySelector('[data-garage-toggle]');
    const status = bar.querySelector('[data-garage-status]');
    const cta = bar.querySelector('[data-garage-open]');
    const cards = grid.querySelectorAll('product-card, .card-product');

    const filterOn = !!scooter && readToggle();
    toggle.checked = filterOn;
    toggle.disabled = !scooter;

    let total = 0, specific = 0, universal = 0;
    cards.forEach((card) => {
      total += 1;
      if (!filterOn) {
        card.removeAttribute('data-garage-hidden');
        card.style.display = '';
        return;
      }
      const kind = classifyCard(card, scooter);
      if (kind === 'specific') {
        card.removeAttribute('data-garage-hidden');
        card.style.display = '';
        specific += 1;
      } else if (kind === 'universal') {
        card.removeAttribute('data-garage-hidden');
        card.style.display = '';
        universal += 1;
      } else {
        card.setAttribute('data-garage-hidden', '');
        card.style.display = 'none';
      }
    });

    if (!scooter) {
      status.textContent = T.no_garage;
      cta.removeAttribute('hidden');
    } else if (filterOn) {
      const scooterLabel = scooter.variantName || scooter.model || scooter.brandName || '';
      let tpl;
      if (specific > 0) {
        tpl = T.filter_status_specific;
      } else if (universal > 0) {
        tpl = T.filter_status_no_specific;
      } else {
        tpl = T.filter_status_none;
      }
      status.textContent = tpl
        .replace('{specific}', String(specific))
        .replace('{universal}', String(universal))
        .replace('{total}', String(total))
        .replace('{scooter}', scooterLabel);
      cta.setAttribute('hidden', '');
    } else {
      status.textContent = T.filter_status_off;
      cta.setAttribute('hidden', '');
    }
  }

  function init() {
    const grids = getGrids();
    if (!grids.length) return;
    grids.forEach((grid) => {
      const bar = injectToolbar(grid);
      const toggle = bar.querySelector('[data-garage-toggle]');
      const cta = bar.querySelector('[data-garage-open]');
      toggle.addEventListener('change', () => {
        writeToggle(toggle.checked);
        applyFilter(grid, bar);
      });
      cta.addEventListener('click', () => {
        if (window.evGarage && typeof window.evGarage.open === 'function') {
          window.evGarage.open();
        }
      });
      applyFilter(grid, bar);
    });

    document.addEventListener('garage:change', (e) => {
      // When the customer picks a scooter (detail is truthy), auto-enable the
      // filter — picking a scooter implies "show me only fitting parts".
      // When they clear, force the filter off so all cards are visible again.
      // Without this the filter would keep its stale sessionStorage value,
      // causing the grid to not react to the first save after an empty state.
      const hasScooter = !!(e && e.detail);
      writeToggle(hasScooter);
      grids.forEach((grid) => {
        const bar = grid.previousElementSibling;
        if (bar && bar.hasAttribute('data-garage-toolbar')) applyFilter(grid, bar);
      });
    });
    // Refresh signal from peer features (e.g. garage-page.js) — they have
    // already updated state, we just need to re-read it.
    document.addEventListener('garage:refresh', () => {
      grids.forEach((grid) => {
        const bar = grid.previousElementSibling;
        if (bar && bar.hasAttribute('data-garage-toolbar')) applyFilter(grid, bar);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
